import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    useOrganizationCareCoordinators,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type CareCoordinatorRead } from '@libraries/typescript/api_client';

// Special ID for filtering by unassigned (no care coordinator)
export const UNASSIGNED_CARE_COORDINATOR_ID = '__unassigned__';

export function formatCoordinatorName(
    coordinator: CareCoordinatorRead,
): string {
    // Prefer linked user's name when available
    const firstName =
        coordinator.linked_user?.first_name ?? coordinator.first_name;
    const lastName =
        coordinator.linked_user?.last_name ?? coordinator.last_name;
    const parts = [firstName, lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Unnamed';
}

export function toggleCareCoordinatorSelection(
    selectedIds: Array<string> | null,
    coordinatorId: string,
): Array<string> {
    const currentIds = selectedIds ?? [];
    if (currentIds.includes(coordinatorId)) {
        return currentIds.filter((id) => id !== coordinatorId);
    }
    return [...currentIds, coordinatorId];
}

type UseCareCoordinatorFilterOptions = {
    readonly enabled?: boolean;
    readonly onFilterChange?: () => void;
    // If provided, use these IDs as initial selection instead of auto-detecting user's coordinator
    readonly initialSelectedIds?: Array<string>;
    // If provided, persist selection to localStorage under this key prefix
    // Full key: `${localStorageKey}:${organizationId}`
    readonly localStorageKey?: string;
};

export function useCareCoordinatorFilter(
    options: UseCareCoordinatorFilterOptions = {},
) {
    const {
        enabled = true,
        onFilterChange,
        initialSelectedIds,
        localStorageKey,
    } = options;
    const organizationId = useOrganizationId();
    const { data: user } = useUser({ reason: 'care-coordinator-filter' });

    const { data: careCoordinators } = useOrganizationCareCoordinators(
        enabled ? organizationId : null,
        { reason: 'care-coordinator-filter' },
    );

    // null = uninitialized (will default to user's coordinator)
    // [] = explicitly cleared (show all)
    // [id1, id2] = specific coordinators selected
    const [selectedIds, setSelectedIds] = useState<Array<string> | null>(
        // If initialSelectedIds provided, use them; otherwise null for auto-init
        initialSelectedIds !== undefined ? initialSelectedIds : null,
    );
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    // localStorage persistence
    const storageKey =
        localStorageKey && organizationId
            ? `${localStorageKey}:${organizationId}`
            : null;
    const previousStorageKeyRef = useRef<string | null>(storageKey);

    // Read from localStorage on mount and org switch
    useEffect(() => {
        if (!storageKey) {
            return;
        }
        setSelectedIds(null);
        const stored = localStorage.getItem(storageKey);
        if (stored !== null) {
            try {
                setSelectedIds(JSON.parse(stored) as Array<string>);
            } catch {
                // Invalid stored value, fall through to auto-init
            }
        }
    }, [storageKey]);

    // Persist to localStorage on change
    useEffect(() => {
        if (!storageKey || selectedIds === null) {
            previousStorageKeyRef.current = storageKey;
            return;
        }
        // Skip persisting when storage key just changed (org switch)
        if (previousStorageKeyRef.current !== storageKey) {
            previousStorageKeyRef.current = storageKey;
            return;
        }
        localStorage.setItem(storageKey, JSON.stringify(selectedIds));
    }, [selectedIds, storageKey]);

    // Sync with initialSelectedIds when it changes AFTER initial mount
    // (e.g., dialog reopened with new selection from parent)
    // Use a stable key to detect changes: 'undefined' for no initial, 'empty' for [], or comma-joined IDs
    const initialIdsKey =
        initialSelectedIds === undefined
            ? 'undefined'
            : initialSelectedIds.length === 0
              ? 'empty'
              : initialSelectedIds.join(',');
    const isFirstMount = useRef(true);
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        // After first mount, sync with parent's selection changes
        if (initialSelectedIds !== undefined) {
            setSelectedIds(initialSelectedIds);
        } else {
            // Reset to null to trigger auto-init to user's coordinator
            setSelectedIds(null);
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- only sync when initialSelectedIds reference changes
    }, [initialIdsKey]);

    // Find the current user's linked care coordinator
    const myCareCoordinator = useMemo(() => {
        if (!user?.id || !careCoordinators) {
            return null;
        }
        return (
            careCoordinators.find(
                (cc) => cc.user_id === user.id && cc.is_active,
            ) ?? null
        );
    }, [user?.id, careCoordinators]);

    // Auto-initialize filter to user's care coordinator when data loads
    // Only if no initialSelectedIds were provided
    useEffect(() => {
        if (
            initialSelectedIds === undefined &&
            selectedIds === null &&
            myCareCoordinator
        ) {
            setSelectedIds([myCareCoordinator.id]);
        }
    }, [selectedIds, myCareCoordinator, initialSelectedIds]);

    // Compute the filter IDs and unassigned flag to pass to APIs
    const { filterIds, includeUnassigned } = useMemo(() => {
        if (selectedIds === null) {
            return {
                filterIds: myCareCoordinator
                    ? [myCareCoordinator.id]
                    : undefined,
                includeUnassigned: false,
            };
        }
        if (selectedIds.length === 0) {
            return {
                filterIds: undefined,
                includeUnassigned: false,
            };
        }

        // Check if "unassigned" is selected and filter it out of actual IDs
        const hasUnassigned = selectedIds.includes(
            UNASSIGNED_CARE_COORDINATOR_ID,
        );
        const actualIds = selectedIds.filter(
            (id) => id !== UNASSIGNED_CARE_COORDINATOR_ID,
        );

        // When only unassigned is selected (no actual coordinator IDs), return empty array
        // so the API filters to only unassigned. When nothing is selected, return undefined
        // to show all.
        return {
            filterIds:
                actualIds.length > 0
                    ? actualIds
                    : hasUnassigned
                      ? []
                      : undefined,
            includeUnassigned: hasUnassigned,
        };
    }, [selectedIds, myCareCoordinator]);

    // Value to pass to child components that need to sync state
    // (preserves "show all" as [] instead of undefined)
    const selectedIdsForSync = selectedIds === null ? undefined : selectedIds;

    // Display value for the coordinator filter combobox
    const displayValue = useMemo(() => {
        if (selectedIds === null && myCareCoordinator) {
            return formatCoordinatorName(myCareCoordinator);
        }
        if (!careCoordinators || selectedIds === null) {
            return null;
        }
        if (selectedIds.length === 0) {
            return null;
        }
        const names = selectedIds
            .map((id) => {
                if (id === UNASSIGNED_CARE_COORDINATOR_ID) {
                    return 'No Care Coordinator';
                }
                const cc = careCoordinators.find((c) => c.id === id);
                return cc ? formatCoordinatorName(cc) : null;
            })
            .filter(Boolean);
        return names.length > 0 ? names.join(', ') : null;
    }, [careCoordinators, selectedIds, myCareCoordinator]);

    const handleSelect = useCallback(
        (coordinatorId: string) => {
            setSelectedIds((current) =>
                toggleCareCoordinatorSelection(current, coordinatorId),
            );
            onFilterChange?.();
        },
        [onFilterChange],
    );

    const handleShowAll = useCallback(() => {
        setSelectedIds([]);
        setIsOpen(false);
        onFilterChange?.();
    }, [onFilterChange]);

    const isCoordinatorSelected = useCallback(
        (coordinatorId: string) => {
            return (
                selectedIds?.includes(coordinatorId) ??
                (selectedIds === null &&
                    myCareCoordinator?.id === coordinatorId)
            );
        },
        [selectedIds, myCareCoordinator],
    );

    const isShowingAll = selectedIds?.length === 0;

    // Reset to initial state (useful when dialog reopens)
    const resetToInitial = useCallback(() => {
        if (initialSelectedIds !== undefined) {
            setSelectedIds(initialSelectedIds);
        } else {
            // Reset to null to trigger auto-init to user's coordinator
            setSelectedIds(null);
        }
    }, [initialSelectedIds]);

    return {
        careCoordinators,
        myCareCoordinator,
        selectedIds,
        filterIds,
        includeUnassigned,
        selectedIdsForSync,
        displayValue,
        isOpen,
        setIsOpen,
        searchValue,
        setSearchValue,
        handleSelect,
        handleShowAll,
        isCoordinatorSelected,
        isShowingAll,
        formatCoordinatorName,
        resetToInitial,
    };
}
